<template>
  <div class="h-full">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loading-spinner diameter="64px"/>
    </div>

    <alert-badge
        v-else-if="error.title"
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <div class="flex bg-white mx-4">
      <div class="cart w-3/4 mt-10 px-6">
        <div class="header h-14 flex flex-1 justify-between gap-2">
          <div class="search-box h-full w-full">
            <el-autocomplete
                v-model="itemSearchTerm"
                :fetch-suggestions="searchItems"
                :placeholder="t('searchItems')"
                @select="addItem"
                class="w-full h-full"
                value-key="name"
                @keypress.enter.prevent="onSearchSelectPressedEnter"
                @keyup.enter.prevent="onSearchSelectKeyupEnter"
                :trigger-on-focus="false"
                highlight-first-item
                :prefix-icon="Search"
                ref="searchInput"
            >
              <template #default="{ item }">
                <div
                    :class="{ 'flex-row-reverse': $ctx.locale == 'ar' }"
                    class="flex justify-between"
                >
                  <div>{{ item.name }}</div>
                  <div v-if="!item.unlimitedStock">
                    {{ item.currentLocationStock.quantity }}
                  </div>
                </div>
              </template>
            </el-autocomplete>
          </div>
          <div>
            <el-switch
                v-model="appendToOrderItems"
                inline-prompt
                active-text="+"
                inactive-text="="
            />
          </div>
        </div>

        <div class="flex flex-row w-full overflow-x-auto pb-4"
             v-if="top8Items.length > 0">
          <div
              v-for="item in top8Items"
              :key="item.id"
              class="bg-gray-100 p-2 mx-2 text-nowrap rounded cursor-pointer hover:bg-gray-400 hover:text-white select-none"
              @click="addItem(item)"
          >
            {{ item.name }}
          </div>
        </div>

        <div class="items-table w-full mt-6 overflow-y-scroll flex flex-col">
          <el-table
              :table-layout="'fixed'"
              :data="order.orderItems"
              stripe
              border
              style="width: 100%"
              height="100%"
              class="card rounded"
              header-cell-class-name="my-header"
          >
            <el-table-column :min-width="90" prop="item.externalID"
                             :label="t('itemCode')"/>
            <el-table-column :min-width="150" :label="t('itemName')">
              <template #default="scope">
                <div class="flex items-center justify-between">
                  <div class="truncate">
                    <span>
                      {{ scope.row.item.name }}
                    </span>
                  </div>
                  <div style="width: 30px">
                    <span v-if="scope.row.item.isSerialized">
                      <img src="../../../assets/serials.png" alt="serials icon"
                           class="item-serial"/>
                    </span>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column type=index :min-width="145" prop="quantity"
                             :label="t('quantity')">
              <template #default="scope">
                <QuantityInput
                    :item="scope.row.item"
                    :packInfo="scope.row.packInfo"
                    type="el-input-number"
                    inputClass="checkout-quantity-el-input-number"
                    :usePackQuantity="false"
                    @onPack="(packInfo) => {onPack(packInfo, scope.row)}"
                    v-model="scope.row.quantity">
                </QuantityInput>
              </template>
            </el-table-column>
            <el-table-column :min-width="100" :label="t('notes')"
                             align="center">
              <template #default="scope">
                <el-input id="notes" v-model="scope.row.notes" show-word-limit
                          clearable>
                </el-input>
              </template>
            </el-table-column>
            <el-table-column :min-width="80" :label="t('delete')"
                             align="center">
              <template #default="scope">
                <span class="text-red-600 cursor-pointer"
                      @click="order.removeItem(scope.$index)">
                  <font-awesome-icon icon="trash"/>
                </span>
              </template>
            </el-table-column>
          </el-table>
          <div class="mb-2 mt-2">
            <div>{{ t('notes') }}</div>
            <div>
              <el-input v-model="order.notes" :rows="2" type="textarea"
                        placeholder=""/>
            </div>
          </div>
        </div>
      </div>

      <div class="aside w-1/4 border-l-2 flex flex-col mt-10 px-4">
        <div class="order-info my-4 bg-white">
          <div class="mt-2 cashier-info flex justify-between">
            <h1>{{ t('user') }}</h1>
            <h1>{{ ctx.currentUser.fullName }}</h1>
          </div>

          <el-divider/>

          <div class="p-4 bg-light-blue flex justify-between items-center">
            <div class="flex flex-col">
              <h1 class="text-l text-primary">
                {{ vendorName === '' ? t('noVendor') : vendorName }}
              </h1>
            </div>
            <font-awesome-icon
                v-if="vendorName"
                icon="times"
                class="text-primary cursor-pointer"
                @click="clearVendor"
            />
          </div>


          <div class="w-full self-end mt-1">
            <horizontal-button
                :title="t('buttons.addVendor')"
                faIcon="plus"
                styles="border border-primary text-primary button-secondary"
                @click="openVendorDialog"
                :rounded="false"
            />
          </div>

          <el-divider/>


          <div class="mt-2 grid grid-cols-5 gap-3 justify-between">
            <h1 class="col-span-2">{{ t('customer') }}</h1>
            <el-select
                filterable
                clearable
                class="col-span-3"
                value-key="name"
                placeholder=""
                v-model="selectedCustomer"
                @change="onSelectedCustomerChanged"
                remote
                :remote-method="searchCustomer"
            >
              <el-option
                  v-for="option in customers"
                  :key="option.id"
                  :label="option.name"
                  :value="option"
              ></el-option>
            </el-select>
          </div>
          <div class="mt-2 grid grid-cols-5 gap-3 justify-between">
            <h1 class="col-span-2">{{ t('manualNumber') }}</h1>
            <div class="col-span-3">
              <el-input v-model="order.manualNumber"/>
            </div>
          </div>
          <div class="mt-2 grid grid-cols-5 gap-3 justify-between">
            <h1 class="col-span-2">{{ t('dueDate2') }}</h1>
            <div class="col-span-3">
              <el-date-picker
                  v-model="order.dueDate"
                  type="date"
                  style="width: 100%"
              />
            </div>
          </div>
          <div class="mt-2 grid grid-cols-5 gap-3 justify-between">
            <h1 class="col-span-2">{{ t('pouchID') }}</h1>
            <div class="col-span-3">
              <el-input v-model="order.pouchID"/>
            </div>
          </div>

          <el-divider/>

          <div class="items-total flex justify-between mt-1">
            <h1>{{ t('totalItems') }}</h1>
            <h1 v-if="order.orderItems.length">{{ getTotalItemsCount() }}</h1>
          </div>

          <el-divider/>



          <div class="action-buttons">
            <horizontal-button
                v-if="order.orderItems.length"
                class="my-4"
                :title="t('buttons.cancel')"
                faIcon="cancel"
                styles="border border-red-500 h-12 text-l text-red-500 hover:bg-red-500 hover:text-white"
                @click="resetOrder()"
                :rounded="false"
            />
            <horizontal-button
                :disabled="!order.orderItems.length"
                :title="t('buttons.save')"
                styles="button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-3xl lg:text-2xl text-l font-bold mt-4"
                :rounded="false"
                @click="openCashierPinDialog"
            />
          </div>
          <el-dialog
              :title="t('selectVendor')"
              v-model="vendorDialogOpen"
              width="30%"
              @close="closeVendorDialog"
          >
            <div class="flex items-center justify-center" :dir="$ctx.getDir()">
              <el-select
                  filterable
                  clearable
                  class="w-full"
                  v-model="order.vendorID"
                  value-key="name"
                  remote
                  @change="onVendorNameChange"
                  :remote-method="searchVendors"
              >
                <el-option
                    v-for="option in vendors"
                    :key="option.id"
                    :label="option.name"
                    :value="option.id"
                ></el-option>
              </el-select>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button
                    @click="() => {
                      newVendorDialogVisible = true
                    }"
                  >
                  <div class="flex items-center gap-2">
                    <span class="fa fa-plus"></span>
                    <span>{{ t('newVendor') }}</span>
                  </div>
                </el-button>
                <el-button type="primary" @click="closeVendorDialog">{{
                    t('buttons.confirm')
                  }}</el-button>
              </span>
            </template>
          </el-dialog>
          <NewVendorDialog v-model="newVendorDialogVisible" @onOpen="() => {vendorDialogOpen = false}" @onSubmit="onNewVendor"/>
          <el-dialog v-model="cashierPinDialogOpen" width="30%">
            <div>
              <!-- Enter the pin -->
              <div class="my-2">
                <p>{{ t('enterCashierPin') }}</p>
              </div>

              <!-- password input up to 6 chars -->
              <div class="my-2">
                <el-input
                    id="password"
                    v-model="cashierPin"
                    type="password"
                    required
                    show-password
                    autofocus
                    dir="ltr"
                    :maxlength="4"
                    :minlength="4"
                    @keyup.enter="submitOrder"
                    ref="cashierPinInput"
                />
              </div>

              <div class="flex items-center justify-between mb-2">
                <horizontal-button
                    class="text-lg complete-payment"
                    :title="t('buttons.confirm')"
                    @click="submitOrder"
                    :rounded="false"
                />
              </div>
            </div>
          </el-dialog>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {nextTick, onMounted, ref} from 'vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import Item from '@/models/inventory/Item'
import {i18nOrderMessages} from '@/models/sales/Order'
import {PurchaseOrder as Order, PurchaseOrderItem as OrderItem} from '@/models/inventory/PurchaseOrder'
import {useHTTP} from '@/plugins/http'
import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
import {useI18n} from 'vue-i18n'
import {localizeFieldName} from '@/plugins/i18n'
import {Register} from '@/models/company/Register'
import {useContext} from '@/plugins/context'
import {useRoute, useRouter} from 'vue-router'
import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import {Route} from '@/router/routes'
import User from '@/models/user/User'
import Location from '@/models/company/Location'
import {ElInput, ElMessage} from 'element-plus'
import {Search} from '@element-plus/icons-vue'
import QuantityInput from "@/views/inventory/items/QuantityInput.vue";
import ItemPack from "@/models/inventory/ItemPack";
import {useSettingsStore} from "@/stores/settingsStore";
import successbeep from "@/assets/sounds/success.mp3"
import errorbeep from "@/assets/sounds/error.mp3"
import Vendor from "@/models/vendor/Vendor";
import NewVendorDialog from "@/views/companies/vendors/NewVendorDialog.vue";
import Customer from "@/models/customer/Customer";

const http = useHTTP()
const alertModal = useAlertModal()
const ctx = useContext()
const router = useRouter()
const route = useRoute()
const selectedIndex = ref()
const itemSearchTerm = ref('')
const currentItemPack = ref<ItemPack | null>()
const appendToOrderItems = ref(false)
const serialSearchTerm = ref('')
const order = ref(new Order())
const pressedEnter = ref(false)
const searchLoading = ref(false)
const error = ref({title: '', body: ''})
const loading = ref(false)
const vendorDialogOpen = ref(false)
const newVendorDialogVisible = ref(false)
const customers = ref(new Array<Customer>())
const selectedCustomer = ref<any>()

const vendors = ref(new Array<Vendor>())
const vendor = ref(null as Vendor | null)
const vendorName = ref('')
const direction = ref('rtl')
const currentOrderItem = ref(new OrderItem(new Item()))

const cashierPin = ref('')
const cashierPinInput = ref<any>()
const cashierPinDialogOpen = ref(false)

const searchInput = ref()
const top8Items = ref([] as Array<Item>)
const tempSearchResult = ref<Array<Item>>(null)

const settingsStore = useSettingsStore();
const {t} = useI18n({
  useScope: 'global',
  messages: {
    en: {
      ...i18nOrderMessages.en,
      newVendor: 'New Vendor',
      notes: 'Notes',
      totalItems: 'Total Items',
      searchItems: 'Enter item barcode or SKU or name',
      delete: 'Delete',
      noVendor: 'No Vendor Selected',
      enterCashierPin: 'Enter cashier pin',
      buttons: {
        addVendor: 'Add Vendor',
        cancel: 'Cancel Order',
        confirm: 'Confirm',
        save: 'Save'
      },

      selectVendor: 'Select a Vendor',
      successMsg: 'Sale complete',

      errCashierPinRequired: 'Cashier pin is required',
      itemNotFound: 'Item Not Found',
      manualNumber: 'Manual No',
      dueDate2: 'Due Date',
      pouchID: 'Pouch ID'
    },
    ar: {
      ...i18nOrderMessages.ar,
      newVendor: 'مورّد جديد',
      notes: 'الملاحظات',
      totalItems: 'إجمالي العناصر',
      searchItems: 'ادخل الباركود او ال SKU او اسم المنتج',
      delete: 'حذف',
      noVendor: 'لم يتم اختيار أي مورّد',
      enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
      buttons: {
        addVendor: 'اضافة مورّد',
        cancel: 'الغاء البيع',
        confirm: 'التأكيد',
        save: 'حفظ'
      },

      selectVendor: 'اختر مورّد',
      successMsg: 'تم اكمال البيع',
      errCashierPinRequired: 'يرجى ادخال رمز الكاشير لاتمام البيع',
      itemNotFound: 'لم يتم ايجاد المنتج',
      manualNumber: 'الرقم الخاص',
      dueDate2: 'التاريخ المتوقع',
      pouchID: 'رقم المحفظة'
    }
  }
})

onMounted(async () => {
  try {
    loading.value = true

    const top8ItemsResp = await http.get<Item[]>('/inventory/items/top8-purchased-items')
    top8Items.value = top8ItemsResp.map((item) => Item.from(item))
  } catch (error) {
    error.value.title = error.title
    error.value.body = error.body
  } finally {
    loading.value = false
    setTimeout(() => {
      searchInput.value.focus()
    }, 100)
  }
})

function addItem(selected: Item) {
  itemSearchTerm.value = ''
  const tmpItemPack = currentItemPack.value;
  currentItemPack.value = null;

  let oi: OrderItem | null;
  if (tmpItemPack) {
    if (tmpItemPack.disabled) {
      return;
    }
    if (selected.isSerialized) {
      alertModal.showDanger({
        title: t('cantSellItemPackWithSerializedItem')
      })
      return;
    }
    oi = order.value.addItemPack(selected, tmpItemPack, appendToOrderItems.value)
  } else {
    oi = order.value.addItem(selected, appendToOrderItems.value)
  }

  serialSearchTerm.value = ''
  appendToOrderItems.value = false;
}

async function searchItems(query: string, cb: Function) {
  searchLoading.value = true;
  serialSearchTerm.value = query
  if (pressedEnter.value) {
    itemSearchTerm.value = ''
  }
  // if an item already exists in the table, don't do remote search
  // const cachedItem = order.value.findByBarcode(query) || order.value.findBySKU(query)
  // if (cachedItem) {
  //   addItem(cachedItem)
  //   cb([])
  //   return
  // }
  try {
    const url = `${Item.ENDPOINT}?type=checkout&q=${query}`
    let items = await http.get<Item[]>(url)
    items = items.map((item) => Item.from(item))
    let temp = items.slice()

    // hide parent item and replace it with its variants
    for (let i = 0; i < items.length; i++) {
      if (items[i].hasVariants) {
        temp = temp.splice(i + 1, 1)
        // temp = [...temp.slice(0, i), ...temp.slice(i + 1)];

        temp.push(...items[i].variants.filter((i) => !i.disabled))
      }
    }

    items = temp
    tempSearchResult.value = temp

    // we need to check if this barcode or sku are packs related so we can select them
    let itemPack = null;
    if (items.length && Array.isArray(items[0].itemPacks)) {
      for (const ip of items[0].itemPacks) {
        if (ip.barcode == query || ip.sku == query) {
          itemPack = ip;
        }
      }
    }

    if (itemPack) {
      currentItemPack.value = itemPack;
    } else {
      currentItemPack.value = null;
    }

    // when entering a barcode, the scanner hits enter by default
    // so if we get back one result, we wanna append that right away
    // it makes the process much faster than make the user select by themselves
    if (pressedEnter.value && items.length === 1) {
      if (settingsStore.getValue('scan_beep') == 'true') {
        const errorAudio = new Audio(successbeep);
        errorAudio.play();
      }
      addItem(items[0])
      cb([])
      tempSearchResult.value = null;
    } else if (pressedEnter.value && items.length == 0) {
      ElMessage.error({
        dangerouslyUseHTMLString: true,
        message: `<h2>${t('itemNotFound')}</h2>`
      })
      if (settingsStore.getValue('scan_error_beep') == 'true') {
        const errorAudio = new Audio(errorbeep);
        errorAudio.play();
      }
      cb([])
      tempSearchResult.value = null;
    } else {
      // show them all options
      cb(items)

    }
  } catch (error) {
    alertModal.showDanger({title: error.title})
    console.log(error)

    cb([])
  }
  pressedEnter.value = false
  searchLoading.value = false;
}

function isOrderValid() {
  const errors: string[] = []
  if (order.value.vendorID === '') {
    errors.push(t('noVendor'))
  }

  if (errors.length) {
    error.value.title = t('validation.inputErrors')
    error.value.body = errors.join('\n')
    return false
  }

  return true
}

function openVendorDialog() {
  ctx.getDir() === 'rtl' ? (direction.value = 'rtl') : (direction.value = 'ltr')

  vendorDialogOpen.value = true
}

function closeVendorDialog() {
  if (selectedIndex.value == undefined) {
    vendorName.value = ''
    vendor.value = null
    order.value.vendorID = undefined
  } else if (selectedIndex.value >= 0) {
    vendorName.value = vendors.value[selectedIndex.value].name
    vendor.value = vendors.value[selectedIndex.value]
    order.value.vendorID = vendors.value[selectedIndex.value].id
  } else {
    vendorName.value = ''
    vendor.value = null
    order.value.vendorID = undefined
  }

  vendorDialogOpen.value = false
}

function onNewVendor(newVendor: Vendor) {
  vendorName.value = newVendor.name
  vendor.value = newVendor
  order.value.vendorID = newVendor.id
  newVendorDialogVisible.value = false
  vendors.value = [newVendor]
  selectedIndex.value = 0
  vendorDialogOpen.value = false;
}

function onVendorNameChange(value: string) {
  selectedIndex.value = vendors.value.findIndex((vendor) => vendor.id === value)
}

function clearVendor() {
  vendorName.value = ''
  order.value.vendorID = ''
  selectedIndex.value = null
  vendors.value = null
}

async function searchVendors(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Vendor.ENDPOINT}?q=${query}`
    const vendorsRes = await http.get<Vendor[]>(url)
    vendors.value = vendorsRes.map((vendor) => Vendor.from(vendor))
  } catch (error) {
    alertModal.showDanger({title: error.title})
    vendors.value = []
  }
}

function getTotalItemsCount() {
  const totalCount = order.value.orderItems.reduce((acc, item) => {
    return acc + item.quantity
  }, 0)

  return totalCount
}

function resetOrder() {
  order.value = new Order()
  selectedIndex.value = null
  pressedEnter.value = false
  itemSearchTerm.value = ''
  error.value = {title: '', body: ''}

  currentOrderItem.value = new OrderItem(new Item())

  vendorDialogOpen.value = false
  vendors.value = new Array<Vendor>()
}

async function openCashierPinDialog() {
  // for validation purposes
  order.value.preparePayload(
      ctx.currentUser as User,
      ctx.currentRegister as Register,
      ctx.currentLocation as Location
  )

  if (!isOrderValid()) {
    return
  }

  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    cashierPinDialogOpen.value = true

    nextTick(() => {
      setTimeout(() => {
        cashierPinInput.value.focus()
      }, 0)
    })
    return;
  }
  submitOrder()
}

async function submitOrder() {
  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    if (cashierPin.value === '') {
      error.value.title = t('errCashierPinRequired')
      cashierPinDialogOpen.value = false
      return
    }
  }

  const requestData = order.value.preparePayload(
      ctx.currentUser as User,
      ctx.currentRegister as Register,
      ctx.currentLocation as Location
  )

  if (!isOrderValid()) {
    return
  } else {
    error.value.title = ''
    error.value.body = ''
    cashierPinDialogOpen.value = false

    try {
      let o: Order;
      o = await http.post<Order>(Order.ENDPOINT, requestData, {
        headers: {pin: cashierPin.value}
      })

      alertModal.showSuccess({title: t('successMsg')})

      await router.push(Route.INVENTORY_PURCHASE_ORDERS_PURCHASE_ORDER_RECEIPT.replace(':id', o.id || ''))
    } catch (err) {
      error.value.title = err.title
      error.value.body = err.body
    }
  }
}

function onPack(packInfo: any, oi: OrderItem) {
  oi.packInfo = packInfo

  if (packInfo == null) {
    return;
  }
}

function onSearchSelectPressedEnter() {
  pressedEnter.value = true;
  setTimeout(() => {
    if (tempSearchResult.value?.length == 0) {
      ElMessage.error({
        dangerouslyUseHTMLString: true,
        message: `<h2>${t('itemNotFound')}</h2>`
      })
      if (settingsStore.getValue('scan_error_beep') == 'true') {
        const errorAudio = new Audio(errorbeep);
        errorAudio.play();
      }
    }
  }, 500)
}

function onSearchSelectKeyupEnter() {
  setTimeout(() => {
    if (!searchLoading.value) {
      pressedEnter.value = false;
    }
  }, 400)
}


async function onSelectedCustomerChanged(event: any) {
  order.value.customerID = selectedCustomer.value? selectedCustomer.value.id: null
}

async function searchCustomer(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Customer.ENDPOINT}?q=${query}`
    const customersRes = await http.get<Customer[]>(url)
    customers.value = customersRes.map((customer) => Customer.from(customer))
  } catch (error) {
    customers.value = []
  }
}

</script>

<style>
@import url(../../../assets/css/index.css);

.bg-primary {
  background-color: var(--primary-bg-color);
}

.border-primary {
  border: 1px solid var(--primary-bg-color);
}

.text-primary {
  color: var(--primary-bg-color);
}

.bg-light-blue {
  background-color: #f2f7ff;
}

.item-name {
  width: 50%;
}

.item-price {
  height: inherit;
  width: auto;
}

.item-quantity {
  width: 15%;
}

.item-total {
  width: 15%;
}

.item-delete {
  width: 5%;
}

.el-radio-button__inner {
  width: 100% !important;
  font-size: 1.2rem;
}

.items-table {
  height: 65vh !important;
}

.el-drawer__header {
  padding-top: 5px;
}
</style>
